import {
  DOMAttributes,
  MutableRefObject,
  createRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../assets/img/spinner.svg";
import { loggerBuilder } from "../services/logger";

const logger = loggerBuilder("suspense-video-player");

const SuspenseVideoPlayer = forwardRef<
  HTMLVideoElement,
  {
    videoPromise: Promise<Blob>;
    thumbnail?: string;
    onPause?: (event: React.SyntheticEvent<HTMLVideoElement, Event>) => void;
    onEnded?: DOMAttributes<HTMLVideoElement>["onEnded"];
  }
>(function SuspenseVideoPlayer(
  { videoPromise, thumbnail, onPause, onEnded },
  ref,
) {
  const { t } = useTranslation();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  // Ref pour le fleg de seeking
  const isSeeking = useRef(false);

  const pauseTimeoutRef = useRef<number | null>(null);

  const videoRef = useMemo(
    () =>
      (ref ??
        createRef<HTMLVideoElement>()) as MutableRefObject<HTMLVideoElement>,
    [ref],
  );

  useEffect(() => {
    if (!videoRef.current?.srcObject)
      void videoPromise.then(
        (m) => {
          if (videoRef.current) videoRef.current.src = URL.createObjectURL(m);
          setVideoLoaded(true);
        },
        (e) => {
          logger.error(e);
          setVideoFailed(true);
        },
      );
  }, [videoRef, videoPromise]);

  useEffect(() => {
    // pour gestion du callback de mise en pause
    const videoElement = videoRef.current;
    if (!videoElement || !onPause) return;

    // Fonction pour annuler un timeout de pause en attente
    const clearPendingPause = () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }
    };

    // Quand la lecture commence, on annule toute intention de pause
    const handlePlay = () => {
      clearPendingPause();
    };

    // Quand on commence à chercher, on annule la pause et on met le flag
    const handleSeeking = () => {
      isSeeking.current = true;
      clearPendingPause();
    };

    // Quand la recherche est terminée, on retire le drapeau
    const handleSeeked = () => {
      isSeeking.current = false;
    };

    // Quand une pause est détectée
    const handlePause = (event: Event) => {
      // On ne réagit que si la vidéo est effectivement en pause
      // et qu'on n'est pas déjà en train de chercher une position.
      if (videoElement.paused && !isSeeking.current) {
        // On attend un tout petit peu pour voir si un événement "seeking" suit
        pauseTimeoutRef.current = window.setTimeout(() => {
          // Si après ce court délai, nous ne sommes toujours pas en mode "seeking",
          // alors c'est une vraie pause.
          if (!isSeeking.current) {
            onPause(
              event as unknown as React.SyntheticEvent<HTMLVideoElement, Event>,
            );
          }
        }, 50); // 50ms semble un délai relativement sûr
      }
    };

    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("seeking", handleSeeking);
    videoElement.addEventListener("seeked", handleSeeked);
    videoElement.addEventListener("pause", handlePause);

    return () => {
      clearPendingPause();
      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("seeking", handleSeeking);
      videoElement.removeEventListener("seeked", handleSeeked);
      videoElement.removeEventListener("pause", handlePause);
    };
  }, [videoRef, onPause]);

  useEffect(() => {
    const ref = videoRef.current;
    function handleContextMenu(event: MouseEvent) {
      event.preventDefault();
    }
    ref?.addEventListener("contextmenu", handleContextMenu);
    return () => {
      ref?.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [videoRef]);

  return (
    <>
      {!videoLoaded && (
        <div className="videoloader">
          <canvas width="16" height="9" />
          {videoFailed ? (
            <p className="videoloader_error">{t("common:VIDEO_LOAD_ERROR")}</p>
          ) : (
            <img className="spinner" src={Spinner} alt="loading" />
          )}
        </div>
      )}
      <video
        poster={thumbnail}
        className="videoplayer"
        ref={videoRef}
        controls
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload"
        hidden={!videoLoaded}
        onEnded={onEnded}
      ></video>
    </>
  );
});

export default SuspenseVideoPlayer;
